import type { ComponentType } from 'react'
import { useIntl, type IntlShape } from 'react-intl'

// The view alone, not the package's index, which would bring the editor into every page.
import { SafeHtml } from '@/shared/rich-text/SafeHtml'

import {
  FacebookIcon,
  InstagramIcon,
  LinkedInIcon,
  SlackIcon,
  XIcon,
  YouTubeIcon,
} from './BrandIcons'
import { useSiteBranding } from './site-branding'

/** A catalog message, or a proper name, which is shown as is in every language. */
type Label = { readonly messageId: string } | { readonly name: string }

function labelText(intl: IntlShape, label: Label): string {
  return 'name' in label ? label.name : intl.formatMessage({ id: label.messageId })
}

/** The legal links from the legacy sign-in page's footer, with its two http links made https. */
const legalLinks: readonly { readonly label: Label; readonly href: string }[] = [
  { label: { name: 'Skillable' }, href: 'https://skillable.com' },
  {
    label: { messageId: 'shell.footer.privacyPolicy' },
    href: 'https://www.skillable.com/privacy/',
  },
  {
    label: { messageId: 'shell.footer.accessibilityStatement' },
    href: 'https://www.skillable.com/accessibility-statement/',
  },
  {
    label: { messageId: 'shell.footer.terms' },
    href: 'https://www.skillable.com/terms-and-conditions/',
  },
]

/** The social links, in the legacy page's order. Marks drawn inline, so nothing loads from another origin. */
const socialLinks: readonly {
  readonly network: string
  readonly href: string
  readonly Icon: ComponentType
}[] = [
  { network: 'Slack', href: 'https://skillable.slack.com/', Icon: SlackIcon },
  { network: 'LinkedIn', href: 'https://www.linkedin.com/company/skillable', Icon: LinkedInIcon },
  { network: 'Facebook', href: 'https://www.facebook.com/Skillable/', Icon: FacebookIcon },
  { network: 'X', href: 'https://twitter.com/skillable', Icon: XIcon },
  { network: 'YouTube', href: 'https://youtube.com/skillable', Icon: YouTubeIcon },
  { network: 'Instagram', href: 'https://instagram.com/skillablelabs', Icon: InstagramIcon },
]

/** The year in the copyright line. A string, so it is not formatted as a number. */
const copyrightYear = '2026'

/**
 * The site footer, on every page: the site's own footer, if it has one, above the Skillable links.
 * Not `position: fixed`: the shell's flex column keeps it at the bottom of a short page and below
 * the content of a long one.
 */
export function AppFooter() {
  const intl = useIntl()
  const footerHtml = useSiteBranding()?.footerHtml ?? null

  return (
    <footer className="border-t border-divider bg-background">
      {footerHtml !== null && (
        <div className="mx-auto max-w-[1600px] px-3 pt-5 text-sm text-text-primary sm:px-6">
          <SafeHtml html={footerHtml} />
        </div>
      )}
      <div className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-between gap-x-8 gap-y-3 px-3 py-5 text-sm text-text-primary sm:px-6">
        <nav
          aria-label={intl.formatMessage({ id: 'shell.footer.legal' })}
          className="flex flex-wrap items-center gap-x-5 gap-y-2"
        >
          {legalLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="inline-flex min-h-6 items-center transition hover:text-primary-main hover:underline"
            >
              {labelText(intl, link.label)}
            </a>
          ))}
          <span className="text-text-secondary">
            {intl.formatMessage({ id: 'shell.footer.copyright' }, { year: copyrightYear })}
          </span>
        </nav>

        <nav
          aria-label={intl.formatMessage({ id: 'shell.footer.social' })}
          className="flex items-center gap-4"
        >
          {socialLinks.map(({ network, href, Icon }) => (
            <a
              key={href}
              href={href}
              aria-label={intl.formatMessage({ id: 'shell.footer.socialLink' }, { network })}
              rel="noreferrer"
              target="_blank"
              className="flex h-8 w-8 items-center justify-center rounded text-text-primary transition hover:text-primary-main"
            >
              <Icon />
            </a>
          ))}
        </nav>
      </div>
    </footer>
  )
}
